import React, { useEffect, useState } from "react";
import ResponsiveImg, { SIZES } from "../components/ResponsiveImg";
import { useParams } from "react-router-dom";
import Link, { LocalizedNavigate as Navigate } from "../components/LocalizedLink";
import { useI18n } from "../i18n/I18nProvider";
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "../components/ui/accordion";
import { Check, MapPin, Home as HomeIcon, Ruler, Calendar, Eye, Maximize2 } from "lucide-react";
import HeroMedia, { HERO_BG } from "../components/HeroMedia";
import ContactForm from "../components/ContactForm";
import Lightbox from "../components/Lightbox";
import SchemaMarkup, {
  buildBreadcrumb,
  buildFAQ,
  buildRealEstateListing,
} from "../components/SchemaMarkup";
import useDocumentMeta from "../hooks/useDocumentMeta";
import { api, resolveImage } from "../lib/api";

export default function DevelopmentDetail() {
  const { slug } = useParams();
  const [d, setD] = useState(null);
  const [others, setOthers] = useState([]);
  const [notFound, setNotFound] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const { t } = useI18n();

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const { data } = await api.get(`/developments/${slug}`);
        if (cancel) return;
        setD(data);
        const { data: all } = await api.get("/developments");
        if (cancel) return;
        setOthers(all.filter((x) => x.slug !== slug).slice(0, 3));
      } catch (_err) {
        if (!cancel) setNotFound(true);
      }
    })();
    return () => {
      cancel = true;
    };
  }, [slug]);

  useDocumentMeta({
    title: d?.seo?.title || "",
    description: d?.seo?.description || "",
    image: d?.image || "",
  });

  if (notFound) return <Navigate to="/developments" replace />;
  if (!d) return <div className="min-h-screen bg-[#F9F6F0]" />;

  const vars = {
    name: d.name,
    price: d.priceFrom,
    completion: d.completion,
    built: d.builtSizes,
    terrace: d.terraceSizes,
    bedrooms: d.bedrooms,
  };
  const detailFaqs = [1, 2, 3, 4].map((n) => ({ q: t(`detail_faq_q${n}`, vars), a: t(`detail_faq_a${n}`, vars) }));
  const shortName = d.name.split(" ")[0];

  return (
    <div className="bg-[#F9F6F0] text-[#1A1A1A]">
      <SchemaMarkup schema={buildRealEstateListing(d)} />
      <SchemaMarkup schema={buildFAQ(detailFaqs)} />
      <SchemaMarkup
        schema={buildBreadcrumb([
          { name: t("home"), url: "/" },
          { name: t("developments"), url: "/developments" },
          { name: d.name, url: `/developments/${d.slug}` },
        ])}
      />

      <section className={`relative h-[85svh] min-h-[560px] overflow-hidden ${HERO_BG}`}>
        <HeroMedia src={resolveImage(d.image)} alt={d.name} overlay="bg-gradient-to-b from-black/45 via-black/20 to-black/55" />
        <div className="relative z-10 h-full flex flex-col justify-end max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 pb-16 lg:pb-24">
          <Link to="/developments" data-testid="back-to-developments" className="text-[10px] tracking-[0.3em] uppercase text-[#F9F6F0]/80 hover:text-[#C5A880] mb-6 inline-block">
            {t("back_all_developments")}
          </Link>
          <p className="text-[10px] tracking-[0.3em] uppercase text-[#E5C99A] mb-3">
            {d.propertyType} · Finca Cortesín
          </p>
          <h1 data-testid="detail-title" className="text-5xl sm:text-6xl lg:text-7xl text-[#F9F6F0] font-light leading-[0.95] max-w-4xl" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            {d.name}
          </h1>
          <p className="text-base lg:text-lg text-[#F9F6F0]/85 mt-6 max-w-2xl">{d.tagline}</p>
        </div>
      </section>

      <section className="py-20 lg:py-28">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
          <div className="lg:col-span-7">
            <p className="text-[10px] tracking-[0.3em] uppercase text-[#73706B] mb-5">— {t("detail_sales_intro")}</p>
            <div
              className="prose prose-stone max-w-none text-xl lg:text-2xl leading-relaxed font-light text-[#1A1A1A]"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
              dangerouslySetInnerHTML={{ __html: d.intro }}
            />
            <div className="border-l-2 border-[#C5A880] pl-6 my-10">
              <p className="text-[10px] tracking-[0.3em] uppercase text-[#73706B] mb-2">
                {t("detail_why_different", { name: shortName })}
              </p>
              <p className="text-base text-[#3D3B38] leading-relaxed">{d.why}</p>
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="border border-[#E5DFD5] p-8 lg:p-10 bg-[#FFFFFF]">
              <p className="text-[10px] tracking-[0.3em] uppercase text-[#73706B] mb-6">{t("detail_key_facts")}</p>
              <dl className="space-y-5">
                {[
                  { icon: HomeIcon, k: t("fact_price_from"), v: d.priceFrom },
                  { icon: HomeIcon, k: t("fact_property_type"), v: d.propertyType },
                  { icon: HomeIcon, k: t("fact_units"), v: d.units },
                  { icon: HomeIcon, k: t("fact_bedrooms"), v: d.bedrooms },
                  { icon: Ruler, k: t("fact_built_sizes"), v: d.builtSizes },
                  { icon: Ruler, k: t("fact_terrace_sizes"), v: d.terraceSizes },
                  { icon: Calendar, k: t("fact_completion"), v: d.completion },
                  { icon: Eye, k: t("fact_views"), v: d.views },
                  { icon: MapPin, k: t("fact_location"), v: d.location },
                ].map((row) => (
                  <div key={row.k} className="grid grid-cols-12 gap-4 pb-4 border-b border-[#E5DFD5] last:border-b-0 last:pb-0">
                    <dt className="col-span-5 text-[10px] tracking-[0.2em] uppercase text-[#73706B] flex items-center gap-2">
                      <row.icon className="w-3.5 h-3.5" strokeWidth={1.25} /> {row.k}
                    </dt>
                    <dd className="col-span-7 text-sm text-[#1A1A1A]">{row.v}</dd>
                  </div>
                ))}
              </dl>
              <Link to={`/contact?request=brochure`} data-testid="detail-brochure-cta" className="block text-center mt-8 bg-[#1A1A1A] text-[#F9F6F0] px-6 py-4 text-[10px] tracking-[0.25em] uppercase hover:bg-[#C5A880] transition-colors">
                {t("detail_send_brochure")}
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 lg:py-28 bg-[#FFFFFF] border-y border-[#E5DFD5]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 mb-16">
            <div className="lg:col-span-5">
              <p className="text-[10px] tracking-[0.3em] uppercase text-[#73706B] mb-4">— {t("detail_amenities_eyebrow")}</p>
              <h2 className="text-4xl lg:text-5xl font-light" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{t("detail_amenities_heading")}</h2>
            </div>
            <div className="lg:col-span-7">
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-y-5 gap-x-8">
                {(d.amenities || []).map((a) => (
                  <li key={a} className="flex items-start gap-3">
                    <Check className="w-4 h-4 text-[#C5A880] mt-1 shrink-0" strokeWidth={1.5} />
                    <span className="text-base text-[#3D3B38] leading-relaxed">{a}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
          {d.gallery && d.gallery.length > 0 && (
            <div data-testid="dev-gallery" className="grid grid-cols-1 md:grid-cols-3 gap-4 lg:gap-6">
              {d.gallery.map((src, i) => (
                <button
                  type="button"
                  key={i}
                  onClick={() => setLightboxIndex(i)}
                  data-testid={`dev-gallery-thumb-${i}`}
                  className={`group relative overflow-hidden cursor-zoom-in ${
                    i === 0 ? "md:col-span-2 md:row-span-2 aspect-[4/3]" : "aspect-[4/3]"
                  }`}
                  aria-label={`Open image ${i + 1} of ${d.gallery.length}`}
                >
                  <ResponsiveImg
                    src={src}
                    sizes={i === 0 ? "(min-width: 768px) 66vw, 100vw" : SIZES.third}
                    alt={`${d.name} gallery ${i + 1}`}
                    loading={i < 3 ? "eager" : "lazy"}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-[1500ms]"
                  />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/15 transition-colors duration-300 flex items-center justify-center">
                    <Maximize2
                      className="w-7 h-7 text-[#F9F6F0] opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                      strokeWidth={1.25}
                    />
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </section>

      <Lightbox
        images={d.gallery || []}
        openIndex={lightboxIndex}
        onClose={() => setLightboxIndex(null)}
      />

      <section className="py-20 lg:py-28">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-4">
              <p className="text-[10px] tracking-[0.3em] uppercase text-[#73706B] mb-4">— {t("detail_buyer_eyebrow")}</p>
              <h2 className="text-3xl lg:text-4xl font-light leading-tight" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{t("detail_buyer_heading", { name: shortName })}</h2>
            </div>
            <div className="lg:col-span-8 space-y-6">
              {(d.buyerProfile || []).map((b, i) => (
                <div key={i} className="flex items-start gap-6 border-b border-[#E5DFD5] pb-6">
                  <span className="text-3xl text-[#C5A880] font-light shrink-0" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <p className="text-base lg:text-lg text-[#3D3B38] leading-relaxed pt-2">{b}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 lg:py-28 bg-[#FFFFFF] border-y border-[#E5DFD5]">
        <div className="max-w-[1100px] mx-auto px-6 md:px-12 lg:px-16">
          <p className="text-[10px] tracking-[0.3em] uppercase text-[#73706B] mb-4">— {t("frequently_asked")}</p>
          <h2 className="text-4xl lg:text-5xl font-light mb-12" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{t("detail_faq_heading", { name: d.name })}</h2>
          <Accordion type="single" collapsible data-testid="detail-faq">
            {detailFaqs.map((f, i) => (
              <AccordionItem value={`faq-${i}`} key={i} className="border-b border-[#E5DFD5]">
                <AccordionTrigger className="text-left text-lg lg:text-xl py-6 hover:text-[#C5A880] hover:no-underline" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{f.q}</AccordionTrigger>
                <AccordionContent className="text-base text-[#73706B] leading-relaxed pb-6">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      <section className="py-20 lg:py-28">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
          <div className="lg:col-span-5">
            <p className="text-[10px] tracking-[0.3em] uppercase text-[#73706B] mb-4">— {t("detail_enquire_eyebrow")}</p>
            <h2 className="text-4xl lg:text-5xl xl:text-6xl font-light leading-[1.05]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              {t("detail_enquire_heading")}
            </h2>
          </div>
          <div className="lg:col-span-7">
            <ContactForm defaultDevelopmentSlug={d.slug} sourcePage={d.name} testIdPrefix="detail-contact" />
          </div>
        </div>
      </section>

      {others.length > 0 && (
        <section className="py-20 bg-[#FFFFFF] border-t border-[#E5DFD5]">
          <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16">
            <p className="text-[10px] tracking-[0.3em] uppercase text-[#73706B] mb-4">— {t("detail_continue")}</p>
            <h2 className="text-3xl lg:text-4xl font-light mb-12" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{t("detail_others_heading")}</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
              {others.map((od) => (
                <Link key={od.slug} to={`/developments/${od.slug}`} data-testid={`related-${od.slug}`} className="group block">
                  <div className="aspect-[4/3] overflow-hidden mb-5">
                    <ResponsiveImg src={od.image} sizes={SIZES.third} alt={od.name} className="w-full h-full object-cover transition-transform duration-[1200ms] group-hover:scale-[1.04]" />
                  </div>
                  <h3 className="text-2xl font-light group-hover:text-[#C5A880]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{od.name}</h3>
                  <p className="text-[10px] tracking-[0.25em] uppercase text-[#73706B] mt-2">{t("from")} {od.priceFrom}</p>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
